import axios from "axios";

// The backend /api is co-located on the same origin the app is served from
// (Kubernetes ingress routes /api -> backend). Using the current origin makes all
// requests SAME-ORIGIN, so cookies are sent and there is no CORS/credentials issue,
// even when the app is opened via different preview domains.
const BACKEND_URL =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Attach the active workspace so the backend resolves the right data owner + access.
export const ACTIVE_WS_KEY = "active_workspace_id";
api.interceptors.request.use((config) => {
  try {
    const ws = localStorage.getItem(ACTIVE_WS_KEY);
    if (ws) config.headers["X-Workspace-Id"] = ws;
  } catch {
    /* ignore */
  }
  return config;
});

// After any successful mutation to a data resource (invoices, rents, documents,
// cashflow, tasks…) or goals/projects, emit a lightweight event so the app can
// live-refresh the per-entity "focus" counts AND entity health dots (sidebar +
// Group HQ) without reloading everything. Covers all mutation sites in one place.
const DATA_MUTATIONS = new Set(["post", "put", "patch", "delete"]);
const DATA_URL_RE = /\/(data\/[\w-]+|goals|projects|tasks)(\/|\?|$)/;
api.interceptors.response.use((res) => {
  try {
    const method = (res.config?.method || "").toLowerCase();
    const url = res.config?.url || "";
    if (DATA_MUTATIONS.has(method) && DATA_URL_RE.test(url) && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("app-data-changed", { detail: { url } }));
    }
  } catch {
    /* ignore */
  }
  return res;
}, (error) => {
  // Surface plan-limit (HTTP 402) responses so the app can prompt an upgrade.
  try {
    const st = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (st === 402 && detail && detail.error === "plan_limit" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("plan-limit", { detail }));
    }
  } catch {
    /* ignore */
  }
  return Promise.reject(error);
});

export default api;
export { BACKEND_URL };
